import * as React from 'react'
import { useState } from 'react'
import { Search, X, ChevronDown, SlidersHorizontal, Tag, Box, Check, Calendar } from 'lucide-react'
import type { Table } from '@tanstack/react-table'
import type { TreatmentType, TreatmentCategory } from '@/lib/api-client'
import { CATEGORY_COLORS } from './treatment-columns'

export type DateFilter = 'all' | 'today' | 'week' | 'month' | 'year'

interface TreatmentToolbarProps<TData> {
  search: string
  onSearchChange: (value: string) => void
  dateFilter: DateFilter
  onDateFilterChange: (value: DateFilter) => void
  categoryId: number | null
  onCategoryChange: (value: number | null) => void
  type: TreatmentType | null
  onTypeChange: (value: TreatmentType | null) => void
  categories: TreatmentCategory[]
  table: Table<TData>
}

const DATE_FILTER_OPTIONS: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
]

const TYPE_OPTIONS: { value: TreatmentType | null; label: string }[] = [
  { value: null, label: 'All Types' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'product', label: 'Product' },
  { value: 'medication', label: 'Medication' },
]

const COLUMN_LABELS: Record<string, string> = {
  select: 'Select',
  name: 'Name',
  categoryName: 'Category',
  type: 'Type',
  variationCount: 'Variations',
  itemCount: 'Items',
  revenue: 'Revenue',
  isActive: 'Status',
  actions: 'Actions',
}

export function TreatmentToolbar<TData>({
  search,
  onSearchChange,
  dateFilter,
  onDateFilterChange,
  categoryId,
  onCategoryChange,
  type,
  onTypeChange,
  categories,
  table,
}: TreatmentToolbarProps<TData>) {
  const [dateOpen, setDateOpen] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [typeOpen, setTypeOpen] = useState(false)
  const [columnsOpen, setColumnsOpen] = useState(false)

  const currentDateLabel = DATE_FILTER_OPTIONS.find(o => o.value === dateFilter)?.label || 'All Time'

  const currentCategoryLabel = categoryId
    ? categories.find(c => c.id === categoryId)?.name || 'Category'
    : 'All Categories'

  const currentTypeLabel = TYPE_OPTIONS.find(o => o.value === type)?.label || 'All Types'

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Search Box - 10% smaller */}
      <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[250px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
        <Search className="w-4 h-4 text-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search treatments..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="border-none outline-none text-[13px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover"
          >
            <X className="w-3.5 h-3.5 text-text-muted" />
          </button>
        )}
      </div>

      {/* Filters - 10% smaller */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Date Filter */}
        <div className="relative">
          <button
            onClick={() => setDateOpen(!dateOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[135px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">{currentDateLabel}</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${dateOpen ? 'rotate-180' : ''}`} />
          </button>

          {dateOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setDateOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[135px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                {DATE_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onDateFilterChange(option.value)
                      setDateOpen(false)
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                      dateFilter === option.value
                        ? 'bg-hover text-text-dark font-medium'
                        : 'text-text-dark hover:bg-hover'
                    }`}
                  >
                    {option.label}
                    {dateFilter === option.value && (
                      <Check className="w-3.5 h-3.5 text-success" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative">
          <button
            onClick={() => setCategoryOpen(!categoryOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 min-w-[145px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark truncate max-w-[90px]">
                {currentCategoryLabel}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
          </button>

          {categoryOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setCategoryOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[180px] max-h-[270px] overflow-y-auto rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <button
                  onClick={() => {
                    onCategoryChange(null)
                    setCategoryOpen(false)
                  }}
                  className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                    categoryId === null
                      ? 'bg-hover text-text-dark font-medium'
                      : 'text-text-dark hover:bg-hover'
                  }`}
                >
                  All Categories
                  {categoryId === null && (
                    <Check className="w-3.5 h-3.5 text-success" />
                  )}
                </button>
                <div className="h-px bg-border my-1" />
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      onCategoryChange(cat.id)
                      setCategoryOpen(false)
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                      categoryId === cat.id
                        ? 'bg-hover text-text-dark font-medium'
                        : 'text-text-dark hover:bg-hover'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: CATEGORY_COLORS[cat.name] || '#6b7280' }}
                      />
                      <span className="truncate">{cat.name}</span>
                      <span className="text-text-muted text-[11px]">({cat.treatmentCount})</span>
                    </div>
                    {categoryId === cat.id && (
                      <Check className="w-3.5 h-3.5 text-success flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Type Filter */}
        <div className="relative">
          <button
            onClick={() => setTypeOpen(!typeOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[126px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">{currentTypeLabel}</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${typeOpen ? 'rotate-180' : ''}`} />
          </button>

          {typeOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setTypeOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[135px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value || 'all'}
                    onClick={() => {
                      onTypeChange(option.value)
                      setTypeOpen(false)
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                      type === option.value
                        ? 'bg-hover text-text-dark font-medium'
                        : 'text-text-dark hover:bg-hover'
                    }`}
                  >
                    {option.label}
                    {type === option.value && (
                      <Check className="w-3.5 h-3.5 text-success" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Column Selector */}
        <div className="relative">
          <button
            onClick={() => setColumnsOpen(!columnsOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[117px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">Columns</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${columnsOpen ? 'rotate-180' : ''}`} />
          </button>

          {columnsOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setColumnsOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[162px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Toggle columns
                </div>
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <button
                      key={column.id}
                      onClick={() => column.toggleVisibility(!column.getIsVisible())}
                      className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                        column.getIsVisible()
                          ? 'text-text-dark'
                          : 'text-text-muted'
                      } hover:bg-hover`}
                    >
                      {COLUMN_LABELS[column.id] || column.id}
                      {column.getIsVisible() && (
                        <Check className="w-3.5 h-3.5 text-success" />
                      )}
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
